import * as React from "react"
import { cn } from "@/lib/utils"
import { ArrowLeft, ExternalLink, Palmtree, Send } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { useBookingT } from "@/hooks/use-translations"
import { useUser } from "@/context/UserContext"
import { useIntegration } from "@/features/integrations/hooks/useIntegration"
import { IntegrationLogo } from "@/features/integrations/components/IntegrationLogo"
import { CategoryBadge } from "@/features/integrations/components/CategoryBadge"
import type { Locale } from "@/features/integrations/api/types"
import { StatusCard } from "./StatusCard"
import { ConfigSection } from "./ConfigSection"
import { ActivityLog } from "./ActivityLog"
import { ActionsFooter } from "./ActionsFooter"
import { SetupRequirementsSection } from "./SetupRequirementsSection"
import { ConnectGuideSection } from "./ConnectGuideSection"
import { RequestIntegrationModal } from "../catalog/RequestIntegrationModal"
import { isHrisProvider, type HrisEmployeeFilter } from "@/features/integrations/hris/hrisApi"
import { isAccountingProvider, type AccountingView } from "@/features/integrations/accounting/accountingApi"
import { AccountingPanel } from "./accounting/AccountingPanel"
import { HrisPanel, type HrisView } from "./hris/HrisPanel"
import { HrisAccessGate } from "./hris/HrisAccessGate"
import { HRIS_PANEL_ANCHOR, HrisHeaderAction, useHrisHeaderState } from "./hris/HrisHeaderAction"

type Props = {
  integrationId: string
  onBack: () => void
  hrisView?: HrisView
  hrisFilter?: HrisEmployeeFilter
  onHrisNavigate?: (view: HrisView, filter?: HrisEmployeeFilter) => void
  accountingView?: AccountingView
  onAccountingNavigate?: (view: AccountingView) => void
}

function pickLocale(lang?: string): Locale {
  return lang === "ru" ? "ru" : "en"
}

const AUTH_MODE_LABELS: Record<string, string> = {
  OAUTH2: "OAuth 2.0",
  OAUTH2_CC: "OAuth (Client Credentials)",
  OAUTH1: "OAuth 1.0",
  API_KEY: "API Key",
  BASIC: "Basic Auth",
  JWT: "JWT",
  TWO_STEP: "Two-Step Auth",
  APP: "App Auth",
  APP_STORE: "App Store",
  CUSTOM: "Custom",
  NONE: "No Auth",
  SIGNATURE: "Signature",
  TBA: "Token-Based Auth",
  MCP_OAUTH2: "MCP OAuth 2.0",
  MCP_OAUTH2_GENERIC: "MCP OAuth (Generic)",
  INSTALL_PLUGIN: "Install Plugin",
  BILL: "Bill Auth",
}

function formatAuthMode(mode: string): string {
  return AUTH_MODE_LABELS[mode] ?? mode
}

/**
 * Stable marker (matched against a capability's English copy) for the HRM
 * time-off / "who's on vacation" capability, so it can be highlighted on
 * HRIS provider pages instead of blending into the generic bullet list.
 */
const TIME_OFF_CAPABILITY_MARKER = "on vacation"

export function DetailView({ integrationId, onBack, hrisView = "overview", hrisFilter, onHrisNavigate, accountingView = "overview", onAccountingNavigate }: Props) {
  const t = useBookingT()
  const { user } = useUser()
  const locale = pickLocale(user?.lang)
  const { isLoading, error, item, connection, activity } = useIntegration(integrationId)
  const [requestOpen, setRequestOpen] = React.useState(false)
  const hrisHeader = useHrisHeaderState(isHrisProvider(integrationId) ? integrationId : "")
  // Once the HR system is connected the page is about the connection, not a sales pitch.
  const pitch = !(isHrisProvider(integrationId) && hrisHeader?.connection)

  if (isLoading) {
    return <DetailSkeleton />
  }

  if (error || !item) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="size-4" />
          {t("integrations_back_to_catalog", "Back to catalog")}
        </Button>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-error-700">
              {t("integrations_load_error", "Failed to load integration")}
              {error?.message ? `: ${error.message}` : ""}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const longDesc = item.long_description?.[locale] ?? item.short_description?.[locale]

  // Pull the time-off / "on vacation" capability out of the list so it can be
  // rendered as a highlighted, full-width row above the regular capabilities.
  const capabilities = item.capabilities ?? []
  const timeOffCapability = capabilities.find((cap) =>
    cap.en?.toLowerCase().includes(TIME_OFF_CAPABILITY_MARKER)
  )
  const otherCapabilities = capabilities.filter(
    (cap) => cap !== timeOffCapability
  )

  const handleConnect = () => toast.info(t("integrations_connect_pending", "Connect flow will be available soon"))
  const handleReconnect = () => toast.info(t("integrations_reconnect_pending", "Reconnect flow will be available soon"))
  const handleDisconnect = () => toast.success(t("integrations_disconnect_success", "Integration disconnected"))
  const handleTest = () => {}

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" onClick={onBack} className="-ml-2">
          <ArrowLeft className="size-4" />
          {t("integrations_back_to_catalog", "Back to catalog")}
        </Button>
        {isAccountingProvider(item.id) ? null : isHrisProvider(item.id) ? (
          <HrisHeaderAction state={hrisHeader} providerName={item.name} onRequest={() => setRequestOpen(true)} />
        ) : (
          <Button
            variant="outline"
            onClick={() => setRequestOpen(true)}
            className="shrink-0"
          >
            <Send className="size-4" />
            {t("integrations_btn_request", "Request integration")}
          </Button>
        )}
        {/* Hidden until we have first-party docs.
        {item.documentation_url && (
          <Button variant="outline" asChild>
            <a href={item.documentation_url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="size-4" />
              {t("integrations_detail_documentation", "Documentation")}
            </a>
          </Button>
        )}
        */}
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 max-md:items-start md:flex-row md:items-center md:justify-between">
            <div className={cn("flex gap-4", pitch && longDesc ? "items-start" : "items-center")}>
              <IntegrationLogo src={item.logo} alt={item.name} size="lg" />
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-semibold leading-none text-neutral-900">{item.name}</h1>
                  <CategoryBadge category={item.category} />
                  {item.auth_mode && !isHrisProvider(item.id) && !isAccountingProvider(item.id) && (
                    <span className="text-xs uppercase tracking-wide text-neutral-700">
                      {formatAuthMode(item.auth_mode)}
                    </span>
                  )}
                </div>
                {pitch && longDesc && <p className="max-w-2xl text-sm text-neutral-700">{longDesc}</p>}
              </div>
            </div>
          </div>

          {pitch && item.capabilities && item.capabilities.length > 0 && (
            <div className="mt-6 border-t border-neutral-200 pt-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-700">
                {t("integrations_detail_capabilities", "Capabilities")}
              </h2>
              <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {timeOffCapability && (
                  <li className="flex items-center gap-2.5 rounded-md border border-warning-300 bg-warning-50 p-2.5 text-sm font-medium text-neutral-900 sm:col-span-2">
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-warning-100 text-warning-700">
                      <Palmtree className="size-4" />
                    </span>
                    <span className="flex-1">{timeOffCapability[locale]}</span>
                    <span className="shrink-0 rounded-full bg-warning-500 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                      {t("integrations_capability_new", "New")}
                    </span>
                  </li>
                )}
                {otherCapabilities.map((cap, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-neutral-900">
                    <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-brand-500" />
                    {cap[locale]}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {isAccountingProvider(item.id) ? (
        <AccountingPanel providerName={item.name} view={accountingView} onNavigate={(view) => onAccountingNavigate?.(view)} />
      ) : isHrisProvider(item.id) ? (
        <div id={HRIS_PANEL_ANCHOR} className="scroll-mt-4">
        <HrisAccessGate providerName={item.name}>
          <HrisPanel
            provider={item.id}
            providerName={item.name}
            view={hrisView}
            employeesFilter={hrisFilter}
            onNavigate={(view, filter) => onHrisNavigate?.(view, filter)}
          />
        </HrisAccessGate>
        </div>
      ) : (
        <>
          {connection && <StatusCard item={item} connection={connection} />}

          {!connection && item.setup_fields.length > 0 && (
            <SetupRequirementsSection fields={item.setup_fields} />
          )}

          {!connection && item.connect_guide && (
            <ConnectGuideSection guide={item.connect_guide} fullDocsUrl={item.docs_connect_url} />
          )}

          {connection && <ConfigSection integrationId={item.id} connection={connection} />}

          {connection && activity.length > 0 && <ActivityLog events={activity} />}

          <ActionsFooter
            integrationName={item.name}
            connection={connection}
            onConnect={handleConnect}
            onReconnect={handleReconnect}
            onDisconnect={handleDisconnect}
            onTest={handleTest}
          />
        </>
      )}

      <RequestIntegrationModal
        open={requestOpen}
        onOpenChange={setRequestOpen}
        initialAppName={item.name}
      />
    </div>
  )
}

function DetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-40" />
      <Skeleton className="h-32 rounded-lg" />
      <Skeleton className="h-40 rounded-lg" />
      <Skeleton className="h-64 rounded-lg" />
    </div>
  )
}
